import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, ArrowUpDown } from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { SortType } from "../sort/SortBy";

const SORT_FIELDS = [
  { id: "", label: "Default" },
  { id: "name", label: "Name" },
  { id: "start", label: "Created Date" },
  { id: "lastActivity", label: "Last Activity" },
  { id: "totalActivities", label: "Activity Count" },
  { id: "totalEmails", label: "Email Count" },
  // { id: "estimatedRevenue", label: "Deal Value" },
];

interface BoardSortSettingsProps {
  sorts: any;
  onChangeSort: (sorts: SortType[]) => void;
}

const BoardSortSettings = ({ sorts, onChangeSort }: BoardSortSettingsProps) => {
  const { t } = useTranslation();

  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("ASC");

  useEffect(() => {
    if (sorts && sorts.size === 1) {
      const sort = sorts.first();
      if (sort.get("field")) {
        const arrField = sort.get("field").split(".");
        if (arrField.length === 3) {
          setSortField(arrField[1]);
        }
      }
      if (sort.get("direction")) {
        setSortDirection(sort.get("direction"));
      }
    } else {
      setSortField("");
      setSortDirection("ASC");
    }
  }, [sorts]);

  const handleFieldChange = (field: string) => {
    if (field) {
      const existed = sorts.toJS().find((item) => item.field === `data.${field}.value`);
      if (!existed) {
        const newSort = {
          field: `data.${field}.value`,
          direction: "ASC",
        };
        onChangeSort([newSort]);
      }
    } else {
      onChangeSort([]);
    }
    setSortField(field);
  };

  const handleDirectionToggle = () => {
    const newSortDirection = sortDirection === "ASC" ? "DESC" : "ASC";
    const existed = sorts.toJS().find((item) => item.field === `data.${sortField}.value`);
    if (existed) {
      const newSort = {
        field: `data.${sortField}.value`,
        direction: newSortDirection,
      };
      onChangeSort([newSort]);
    }
    setSortDirection(newSortDirection);
  };

  const currentLabel = SORT_FIELDS.find((f) => f.id === sortField)?.label || "Default";

  return (
    <div>
      <Popover>
        <PopoverButton className="w-full flex items-center justify-between space-x-2 p-2 cursor-default rounded-md hover:bg-gray-100 outline-none">
          <div className="flex items-center space-x-2">
            <div>
              <ArrowUpDown className="w-5 h-5" />
            </div>
            <div className="text-nowrap text-sm font-medium">{t("Sort cards")}</div>
          </div>
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-2">
              <div className="text-xs text-gray-500">{t(currentLabel)}</div>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>
        </PopoverButton>
        <PopoverPanel anchor="right start" className="[--anchor-gap:12px] z-20">
          <div className="w-max max-h-80 overflow-y-auto p-2 rounded-md bg-white border border-gray-300 shadow-lg">
            <div className="text-xs font-medium text-gray-400 px-3 py-1 mb-1">{t("Sort by")}</div>
            {SORT_FIELDS.map((field) => (
              <button
                key={field.id}
                onClick={() => handleFieldChange(field.id)}
                className={`w-full text-left text-sm px-3 py-1.5 rounded hover:bg-gray-100 flex items-center justify-between min-w-[180px] ${
                  sortField === field.id ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-700"
                }`}
              >
                <span>{t(field.label)}</span>
                {sortField === field.id && field.id !== "" && (
                  <span className="text-xs text-blue-500 ml-2">
                    {sortDirection === "ASC" ? "↑" : "↓"}
                  </span>
                )}
              </button>
            ))}
            {sortField && (
              <div className="border-t border-gray-100 mt-1 pt-1">
                <button
                  onClick={handleDirectionToggle}
                  className="w-full text-left text-sm px-3 py-1.5 rounded hover:bg-gray-100 text-gray-600"
                >
                  {sortDirection === "ASC"
                    ? t("↑ Ascending → ↓ Descending")
                    : t("↓ Descending → ↑ Ascending")}
                </button>
              </div>
            )}
          </div>
        </PopoverPanel>
      </Popover>
    </div>
  );
};

export default BoardSortSettings;
